import { LayerTypeLayerSummaryProps } from "../../../types";
import { useKeyIndicatorTags } from "react-migration/lib/map/useKeyIndicatorTags";
import { TagColor } from "react-migration/components/Tag";
import { buildCompsQuery } from "react-migration/domains/comparables/util/BuildCompsQuery";
import { IComparablesTransaction, ISortQuery } from "react-migration/lib/typings/comparables/Comparables";
import { getDefaultFilters } from "react-migration/domains/comparables/util/defaultFilters";
import { disabledZooplaFields } from "react-migration/domains/comparables/util/DisabledFilters";
import { formatToCurrency } from "react-migration/lib/util/numberFormat";
import { useHometrackValue } from "./useHometrackValue";
import { useSelectionSidebarLoading } from "../../../Bundle/useSelectionSidebarLoadingState";
import { useRadiusInput } from "react-migration/lib/map/useRadiusInput";
import { useMemo, useState } from "react";
import { convertGeoJsonToPolygonGeoFilter } from "react-migration/domains/comparables/util/Geo";
import { CurrencyCode } from "react-migration/lib/typings/Currency";
import { HometrackValuesSale } from "./HometrackValuesSale";
import { HometrackLayerFilters, HometrackLayerFilterState } from "./HometrackLayerFilters";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { useSnapshot } from "valtio";
import { MeasurementSystem, userStore } from "src/js/stores/user/store";
import { logEventV2, WorkbenchEventName } from "react-migration/lib/util/logEventV2";

const sortCriteria: ISortQuery<IComparablesTransaction> = {
  fieldName: "date_of_transfer",
  sortType: {
    order: "desc",
  },
};

export const HometrackLayerSummarySale = ({
  expanded,
  print,
  selection,
  onLoadingChange,
}: LayerTypeLayerSummaryProps) => {
  const { RadiusInput, bufferedGeometry } = useRadiusInput({
    geometry: selection?.feature?.geometry || null,
    enabled: !!expanded,
    defaultRadius: 300,
    onRadiusChange: (radius) => {
      logEventV2({
        name: WorkbenchEventName.WORKBENCH_FILTER_CLICK,
        properties: { cardName: "comparables", filterName: "radius", filterState: radius },
      });
    },
  });

  const searchPoly = useMemo(
    () => ({
      poly_geo_filter: bufferedGeometry?.geometry ? convertGeoJsonToPolygonGeoFilter(bufferedGeometry.geometry) : null,
    }),
    [bufferedGeometry]
  );

  const [filters, setFilters] = useState<HometrackLayerFilterState<"sale">>({
    displayBy: "IndexedCurrentValue",
    bedrooms: 2,
  });

  const searchCriteria: Parameters<typeof useHometrackValue>[0] = {
    ...buildCompsQuery(getDefaultFilters(), disabledZooplaFields),
    ...searchPoly,
    bedrooms: filters.bedrooms ? { from: filters.bedrooms, to: filters.bedrooms } : undefined,
  };

  const { valueArea: estimatedValueArea, loading: estimatedValueLoading } = useHometrackValue(
    searchCriteria,
    sortCriteria,
    filters.displayBy === "IndexedCurrentValue" ? "VAL_ESTIMATE" : "VAL_LAST"
  );

  const { t } = useTranslation();
  const { unit } = useSnapshot(userStore.user.settings);

  useKeyIndicatorTags(() => {
    if (!estimatedValueArea) return [];
    return [
      {
        tag: `${formatToCurrency({
          amount: estimatedValueArea,
          currency: CurrencyCode.GBP,
          maximumFractionDigits: 0,
        })} /  ${unit === MeasurementSystem.METRIC ? t("comparables.sqm.alt") : t("comparables.sqft.alt")}`,
        color: TagColor.DEFAULT,
      },
    ];
  }, [estimatedValueArea, t, unit]);

  useSelectionSidebarLoading(onLoadingChange, estimatedValueLoading);

  return (
    <div className="flex flex-col gap-y-4">
      <HometrackLayerFilters type="sale" filters={filters} onFilterChange={setFilters} />
      <div className="flex items-center justify-between">{RadiusInput}</div>
      {(print || expanded) && (
        <HometrackValuesSale
          searchCriteria={searchCriteria}
          sortCriteria={sortCriteria}
          displayBy={filters.displayBy}
        />
      )}
    </div>
  );
};

HometrackLayerSummarySale.icon = "icon-lt-hometrack";
